import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck, UserCheck, FileText, Database, Eye, Layers, Lock,
  ArrowRight, Ban, AlertTriangle, ListChecks, ScrollText, Check, X,
  Cpu, BookOpen, Mail,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import HussarTopNav from '../components/HussarTopNav';
import IntegrityCapability from '../components/IntegrityCapability';
import { track } from '../lib/analytics';

/**
 * BezpieczenstwoIZgodnosc — v4.0 Phase 7.
 *
 * Trust page for HussarAi's immigration-record-integrity positioning.
 * Renders the v4.0 doctrine strip, the "Bez automatycznych decyzji"
 * section, the eight safety rules, the review-signals framing, the
 * data-governance doctrine ("Raw data expires. Process intelligence
 * compounds."), boundaries of responsibility, the audit trail section,
 * and a two-column may-do / must-not-do table.
 *
 * Route: /bezpieczenstwo-i-zgodnosc (public, unprotected, zero backend).
 */
const CYAN = '#7DD3FC';
const CYAN_STRONG = '#38BDF8';
const RED_SOFT = 'rgba(248,113,113,0.30)';

const BezpieczenstwoIZgodnosc = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    track('gov_security_page_view', {});
    document.title = t('gov.security.metaTitle');
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', t('gov.security.metaDescription'));

    // v4.0 Phase 8 · Self-referential canonical + hreflang.
    const setLink = (rel, hreflang, href) => {
      const sel = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]`;
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        if (hreflang) el.setAttribute('hreflang', hreflang);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };
    setLink('canonical', null, 'https://hussarai.org/bezpieczenstwo-i-zgodnosc');
    setLink('alternate', 'pl-PL', 'https://hussarai.org/bezpieczenstwo-i-zgodnosc');
    setLink('alternate', 'en-GB', 'https://hussarai.org/bezpieczenstwo-i-zgodnosc?lang=en');
    setLink('alternate', 'x-default', 'https://hussarai.org/bezpieczenstwo-i-zgodnosc');

    return () => {
      // Reset canonical to homepage default when unmounting so SPA route
      // changes don't leak this canonical to the next page.
      const c = document.head.querySelector('link[rel="canonical"]');
      if (c) c.setAttribute('href', 'https://hussarai.org/');
    };
  }, [t]);

  // v4.0 Phase 8 · WebPage + BreadcrumbList (safe-by-design local constant).
  const jsonLdWebPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': 'https://hussarai.org/bezpieczenstwo-i-zgodnosc#page',
    url: 'https://hussarai.org/bezpieczenstwo-i-zgodnosc',
    name: 'Bezpieczeństwo i zgodność dla akt imigracyjnych',
    description:
      'Osiem zasad bezpieczeństwa, brak automatycznych decyzji, spójność akt zamiast oceny człowieka, ślad audytowy. HussarAi przygotowuje akta do decyzji człowieka.',
    inLanguage: 'pl-PL',
    isPartOf: { '@id': 'https://hussarai.org/#website' },
    about: { '@id': 'https://hussarai.org/#org' },
    significantLink: [
      'https://hussarai.org/review-akt-imigracyjnych',
      'https://hussarai.org/moje-akta-imigracyjne',
      'https://hussarai.org/dla-instytucji',
    ],
  };
  const jsonLdBreadcrumbs = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'HussarAi', item: 'https://hussarai.org/' },
      { '@type': 'ListItem', position: 2, name: 'Bezpieczeństwo i zgodność',
        item: 'https://hussarai.org/bezpieczenstwo-i-zgodnosc' },
    ],
  };

  const noAutoItems = t('gov.security.noAutoDecisions.items', { returnObjects: true }) || [];
  const eightRules = t('gov.security.eightRules.rules', { returnObjects: true }) || [];
  const reviewSignalItems = t('gov.security.reviewSignalsV4.items', { returnObjects: true }) || [];
  const boundariesLines = t('gov.security.boundaries.lines', { returnObjects: true }) || [];
  const auditItems = t('gov.security.audit.items', { returnObjects: true }) || [];
  const mayDoItems = t('gov.security.mayVsMustNot.mayDoItems', { returnObjects: true }) || [];
  const mustNotItems = t('gov.security.mayVsMustNot.mustNotItems', { returnObjects: true }) || [];
  const responsibleAiPrinciples = t('gov.security.responsibleAi.principles', { returnObjects: true }) || [];
  const controlMapItems = t('gov.security.responsibleAi.controlMapItems', { returnObjects: true }) || [];
  const legacySections = t('gov.security.sections', { returnObjects: true }) || [];
  const legacyIcons = [UserCheck, ShieldCheck, Lock, Eye, Layers, FileText];

  return (
    <div className="hussar-landing" data-testid="gov-security-page">
      {/* v4.0 Phase 8 · Structured data (safe-by-design local constants). */}
      <script
        type="application/ld+json"
        data-testid="gov-security-jsonld-webpage"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdWebPage) }}
      />
      <script
        type="application/ld+json"
        data-testid="gov-security-jsonld-breadcrumbs"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdBreadcrumbs) }}
      />

      <HussarTopNav active="compliance" />

      <main className="hussar-agenci-main">
        {/* Hero — v4.0 title + subhead */}
        <section className="hussar-agenci-hero" data-testid="gov-security-hero">
          <div className="hussar-agenci-eyebrow">
            <ShieldCheck className="w-3 h-3 inline-block mr-1" />
            {t('gov.security.eyebrow')}
          </div>
          <h1 className="hussar-agenci-h1" data-testid="gov-security-h1">{t('gov.security.h1')}</h1>
          <p className="hussar-agenci-subhead" data-testid="gov-security-subhead">{t('gov.security.subhead')}</p>
        </section>

        {/* v4.0 doctrine strip — 4 anchor lines */}
        <section
          className="hussar-pol-op-section"
          data-testid="gov-security-v4-doctrine"
          style={{
            background: 'rgba(30,41,59,0.55)',
            borderLeft: `3px solid ${CYAN_STRONG}`,
            borderRadius: 14,
            padding: '24px 26px',
          }}
        >
          <p className="hussar-pol-op-body" data-testid="gov-security-v4-doctrine-line-1"
             style={{ color: 'rgba(226,232,240,0.92)', marginBottom: 8 }}>
            {t('gov.security.v4Doctrine.line1')}
          </p>
          <p className="hussar-pol-op-body" data-testid="gov-security-v4-doctrine-line-2"
             style={{ color: 'rgba(226,232,240,0.88)', fontStyle: 'italic', marginBottom: 8 }}>
            {t('gov.security.v4Doctrine.line2')}
          </p>
          <p className="hussar-pol-op-body" data-testid="gov-security-v4-doctrine-line-3"
             style={{ color: CYAN_STRONG, fontWeight: 700, marginBottom: 8 }}>
            {t('gov.security.v4Doctrine.line3')}
          </p>
          <p className="hussar-pol-op-body" data-testid="gov-security-v4-doctrine-line-4"
             style={{ color: 'rgba(226,232,240,0.86)', marginBottom: 0 }}>
            {t('gov.security.v4Doctrine.line4')}
          </p>
        </section>

        {/* Section 1 — Bez automatycznych decyzji */}
        <section className="hussar-pol-op-section" data-testid="gov-security-no-auto-decisions">
          <h2 className="hussar-agenci-h2" data-testid="gov-security-no-auto-decisions-title">
            <Ban className="w-6 h-6 inline-block mr-2" />
            {t('gov.security.noAutoDecisions.title')}
          </h2>
          <ul className="hussar-pol-op-bullets" data-testid="gov-security-no-auto-decisions-list">
            {noAutoItems.map((item, i) => (
              <li key={item} data-testid={`gov-security-no-auto-decisions-${i}`}>{item}</li>
            ))}
          </ul>
          <p className="hussar-pol-op-body" data-testid="gov-security-no-auto-decisions-human-review"
             style={{ marginTop: '18px', fontWeight: 600, color: CYAN_STRONG }}>
            {t('gov.security.noAutoDecisions.humanReviewLine')}
          </p>
        </section>

        {/* Section 2 — Eight safety rules */}
        <section className="hussar-pol-op-section" data-testid="gov-security-eight-rules">
          <h2 className="hussar-agenci-h2" data-testid="gov-security-eight-rules-title">
            <ShieldCheck className="w-6 h-6 inline-block mr-2" />
            {t('gov.security.eightRules.title')}
          </h2>
          <div className="hussar-agenci-deliverables-grid" data-testid="gov-security-eight-rules-grid">
            {eightRules.map((rule, i) => (
              <Card key={rule.title} className="hussar-agenci-deliverable-card" data-testid={`gov-security-rule-${i + 1}`}>
                <div className="hussar-agenci-deliverable-icon">
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28, borderRadius: 6,
                    background: 'rgba(56,189,248,0.14)', border: '1px solid rgba(56,189,248,0.42)',
                    color: CYAN_STRONG, fontSize: 13, fontWeight: 800,
                  }}>{i + 1}</span>
                </div>
                <h3 className="hussar-agenci-deliverable-title" style={{ fontSize: '15px' }}
                    data-testid={`gov-security-rule-${i + 1}-title`}>
                  {rule.title}
                </h3>
                <p className="hussar-agenci-deliverable-body" data-testid={`gov-security-rule-${i + 1}-body`}>
                  {rule.body}
                </p>
              </Card>
            ))}
          </div>
        </section>

        {/* Section 3 — Review signals */}
        <section className="hussar-pol-op-section" data-testid="gov-security-review-signals-v4">
          <h2 className="hussar-agenci-h2" data-testid="gov-security-review-signals-v4-title">
            <AlertTriangle className="w-6 h-6 inline-block mr-2" />
            {t('gov.security.reviewSignalsV4.title')}
          </h2>
          <p className="hussar-pol-op-body" data-testid="gov-security-review-signals-v4-framing"
             style={{ fontWeight: 600, color: 'rgba(226,232,240,0.92)' }}>
            {t('gov.security.reviewSignalsV4.framing')}
          </p>
          <ul className="hussar-pol-op-bullets" data-testid="gov-security-review-signals-v4-list">
            {reviewSignalItems.map((item, i) => (
              <li key={item} data-testid={`gov-security-review-signal-v4-${i}`}>{item}</li>
            ))}
          </ul>
        </section>

        {/* Section 4 — Data governance doctrine (v4.0 — "Raw data expires. Process intelligence compounds.") */}
        <section className="hussar-pol-op-section" data-testid="gov-security-data-governance">
          <h2 className="hussar-agenci-h2" data-testid="gov-security-dg-title">
            <Database className="w-6 h-6 inline-block mr-2" />
            {t('gov.security.dgTitle')}
          </h2>
          <p className="hussar-pol-op-body" data-testid="gov-security-dg-title-english"
             style={{ fontStyle: 'italic', color: CYAN_STRONG, marginBottom: 12 }}>
            {t('gov.security.dgTitleEnglish')}
          </p>
          <p className="hussar-pol-op-body" data-testid="gov-security-dg-body">
            {t('gov.security.dgBody')}
          </p>
          <p className="hussar-pol-op-body" style={{ marginTop: '16px', fontStyle: 'italic' }}
             data-testid="gov-security-dg-doctrine">
            „{t('gov.security.dgDoctrine')}”
          </p>
          <div className="hussar-agenci-deliverables-grid" style={{ marginTop: '24px' }}>
            <Card className="hussar-agenci-deliverable-card" data-testid="gov-security-dg-card-operational">
              <div className="hussar-agenci-deliverable-icon"><Lock className="w-5 h-5" /></div>
              <h3 className="hussar-agenci-deliverable-title" style={{ fontSize: '15px' }}>
                {t('gov.security.dgOperationalTitle')}
              </h3>
              <p className="hussar-agenci-deliverable-body">
                {t('gov.security.dgOperationalBody')}
              </p>
            </Card>
            <Card className="hussar-agenci-deliverable-card" data-testid="gov-security-dg-card-intelligence">
              <div className="hussar-agenci-deliverable-icon"><Layers className="w-5 h-5" /></div>
              <h3 className="hussar-agenci-deliverable-title" style={{ fontSize: '15px' }}>
                {t('gov.security.dgIntelligenceTitle')}
              </h3>
              <p className="hussar-agenci-deliverable-body">
                {t('gov.security.dgIntelligenceBody')}
              </p>
            </Card>
          </div>
          <p className="hussar-pol-op-body" style={{ marginTop: '20px', fontWeight: 600, color: 'rgba(226,232,240,0.92)' }}
             data-testid="gov-security-dg-principle">
            {t('gov.security.dgPrinciple')}
          </p>
          <p className="hussar-pol-op-body" style={{ marginTop: '10px', fontWeight: 600 }}
             data-testid="gov-security-dg-statement">
            {t('gov.security.dgStatement')}
          </p>
        </section>

        {/* Section 5 — Boundaries of responsibility */}
        <section className="hussar-pol-op-section" data-testid="gov-security-boundaries">
          <h2 className="hussar-agenci-h2" data-testid="gov-security-boundaries-title">
            <ScrollText className="w-6 h-6 inline-block mr-2" />
            {t('gov.security.boundaries.title')}
          </h2>
          <ul className="hussar-pol-op-bullets" data-testid="gov-security-boundaries-list">
            {boundariesLines.map((line, i) => (
              <li key={line} data-testid={`gov-security-boundary-${i}`}>{line}</li>
            ))}
          </ul>
        </section>

        {/* Section 6 — Audit trail */}
        <section className="hussar-pol-op-section" data-testid="gov-security-audit">
          <h2 className="hussar-agenci-h2" data-testid="gov-security-audit-title">
            <ListChecks className="w-6 h-6 inline-block mr-2" />
            {t('gov.security.audit.title')}
          </h2>
          <ul className="hussar-pol-op-bullets" data-testid="gov-security-audit-list">
            {auditItems.map((item, i) => (
              <li key={item} data-testid={`gov-security-audit-${i}`}>{item}</li>
            ))}
          </ul>
        </section>

        {/* Section 7 — What HussarAi may do / must not do */}
        <section className="hussar-pol-op-section" data-testid="gov-security-may-vs-mustnot">
          <h2 className="hussar-agenci-h2" data-testid="gov-security-may-vs-mustnot-title">
            <ShieldCheck className="w-6 h-6 inline-block mr-2" />
            {t('gov.security.mayVsMustNot.title')}
          </h2>
          <div
            data-testid="gov-security-may-vs-mustnot-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 16,
              marginTop: 12,
            }}
          >
            <div data-testid="gov-security-may-do" style={{
              padding: 20, borderRadius: 12,
              background: 'rgba(2,6,23,0.55)',
              border: '1px solid rgba(56,189,248,0.30)',
            }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: CYAN_STRONG, marginTop: 0, marginBottom: 12 }}
                  data-testid="gov-security-may-do-title">
                <Check className="w-4 h-4 inline-block mr-2" />
                {t('gov.security.mayVsMustNot.mayDoTitle')}
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}
                  data-testid="gov-security-may-do-list">
                {mayDoItems.map((item, i) => (
                  <li key={item} data-testid={`gov-security-may-do-${i}`}
                      style={{ fontSize: 14, color: 'rgba(226,232,240,0.88)', display: 'flex', gap: 8 }}>
                    <Check className="w-4 h-4" style={{ color: CYAN_STRONG, flex: '0 0 auto', marginTop: 2 }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div data-testid="gov-security-must-not" style={{
              padding: 20, borderRadius: 12,
              background: 'rgba(2,6,23,0.55)',
              border: `1px solid ${RED_SOFT}`,
            }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#F87171', marginTop: 0, marginBottom: 12 }}
                  data-testid="gov-security-must-not-title">
                <X className="w-4 h-4 inline-block mr-2" />
                {t('gov.security.mayVsMustNot.mustNotTitle')}
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}
                  data-testid="gov-security-must-not-list">
                {mustNotItems.map((item, i) => (
                  <li key={item} data-testid={`gov-security-must-not-${i}`}
                      style={{ fontSize: 14, color: 'rgba(226,232,240,0.88)', display: 'flex', gap: 8 }}>
                    <X className="w-4 h-4" style={{ color: '#F87171', flex: '0 0 auto', marginTop: 2 }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Section 8 — Responsible AI + Security architecture
            (Security Hardening Sprint 2026-02 — documentation-only, no
            behavioural changes). See gov.security.responsibleAi in the
            i18n bundle; the mustNotItems / principles lists carry the
            full doctrine. References NIST CSF 2.0 / OWASP ASVS / OWASP
            LLM as public frameworks only — no certification claim. */}
        <section className="hussar-pol-op-section" data-testid="gov-security-responsible-ai">
          <div className="hussar-agenci-eyebrow" data-testid="gov-security-responsible-ai-eyebrow">
            <Cpu className="w-3 h-3 inline-block mr-1" />
            {t('gov.security.responsibleAi.eyebrow')}
          </div>
          <h2 className="hussar-agenci-h2" data-testid="gov-security-responsible-ai-title">
            <ShieldCheck className="w-6 h-6 inline-block mr-2" />
            {t('gov.security.responsibleAi.title')}
          </h2>
          <p className="hussar-pol-op-body" data-testid="gov-security-responsible-ai-intro">
            {t('gov.security.responsibleAi.intro')}
          </p>

          <h3 className="hussar-agenci-deliverable-title" style={{ marginTop: 24, fontSize: '15px' }}
              data-testid="gov-security-responsible-ai-principles-title">
            {t('gov.security.responsibleAi.principlesTitle')}
          </h3>
          <div className="hussar-agenci-deliverables-grid" data-testid="gov-security-responsible-ai-principles-grid">
            {responsibleAiPrinciples.map((p, i) => (
              <Card key={p.title} className="hussar-agenci-deliverable-card"
                    data-testid={`gov-security-responsible-ai-principle-${i + 1}`}>
                <div className="hussar-agenci-deliverable-icon">
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28, borderRadius: 6,
                    background: 'rgba(56,189,248,0.14)', border: '1px solid rgba(56,189,248,0.42)',
                    color: CYAN_STRONG, fontSize: 13, fontWeight: 800,
                  }}>{i + 1}</span>
                </div>
                <h3 className="hussar-agenci-deliverable-title" style={{ fontSize: '15px' }}
                    data-testid={`gov-security-responsible-ai-principle-${i + 1}-title`}>
                  {p.title}
                </h3>
                <p className="hussar-agenci-deliverable-body"
                   data-testid={`gov-security-responsible-ai-principle-${i + 1}-body`}>
                  {p.body}
                </p>
              </Card>
            ))}
          </div>

          <div style={{
            marginTop: 24, padding: 20, borderRadius: 12,
            background: 'rgba(2,6,23,0.55)',
            border: '1px solid rgba(56,189,248,0.30)',
          }} data-testid="gov-security-control-map">
            <h3 style={{ fontSize: 15, fontWeight: 700, color: CYAN_STRONG, marginTop: 0, marginBottom: 12 }}
                data-testid="gov-security-control-map-title">
              <BookOpen className="w-4 h-4 inline-block mr-2" />
              {t('gov.security.responsibleAi.controlMapTitle')}
            </h3>
            <p className="hussar-pol-op-body" data-testid="gov-security-control-map-intro">
              {t('gov.security.responsibleAi.controlMapIntro')}
            </p>
            <ul className="hussar-pol-op-bullets" data-testid="gov-security-control-map-list">
              {controlMapItems.map((item, i) => (
                <li key={item} data-testid={`gov-security-control-map-${i}`}>{item}</li>
              ))}
            </ul>
            <p className="hussar-pol-op-body"
               data-testid="gov-security-control-map-not-certified"
               style={{ marginTop: 16, fontStyle: 'italic', color: 'rgba(226,232,240,0.82)' }}>
              {t('gov.security.responsibleAi.notCertifiedLine')}
            </p>
            <p className="hussar-pol-op-body"
               data-testid="gov-security-control-map-read-more"
               style={{ marginTop: 12, fontSize: 13, color: 'rgba(226,232,240,0.72)' }}>
              <strong>{t('gov.security.responsibleAi.readMoreLabel')}:</strong>{' '}
              {t('gov.security.responsibleAi.readMoreNote')}
            </p>
          </div>
        </section>

        {/* v4.0 Phase 8 · Doctrine anchor link — trust page → demo. */}
        <section className="hussar-pol-op-section" data-testid="gov-security-report-card-section">
          <div
            data-testid="gov-security-report-card"
            style={{
              padding: 20, borderRadius: 12,
              background: 'rgba(2,6,23,0.55)',
              border: '1px solid rgba(56,189,248,0.30)',
              display: 'grid', gap: 10,
            }}
          >
            <div className="hussar-agenci-eyebrow"
                 data-testid="gov-security-report-card-eyebrow"
                 style={{ margin: 0 }}>
              <Mail className="w-3 h-3 inline-block mr-1" />
              {t('gov.security.reportCard.eyebrow')}
            </div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: CYAN_STRONG, margin: 0 }}
                data-testid="gov-security-report-card-title">
              {t('gov.security.reportCard.title')}
            </h3>
            <p className="hussar-pol-op-body" style={{ margin: 0 }}
               data-testid="gov-security-report-card-body">
              {t('gov.security.reportCard.body')}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginTop: 6 }}>
              <a
                href="/.well-known/security.txt"
                data-testid="gov-security-report-card-link"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 18px', borderRadius: 8, fontWeight: 700,
                  background: CYAN, color: '#0B1220', textDecoration: 'none', fontSize: 14,
                }}
              >
                <FileText className="w-4 h-4" aria-hidden="true" />
                {t('gov.security.reportCard.linkLabel')}
              </a>
              <span style={{ fontSize: 13, color: 'rgba(226,232,240,0.82)' }}
                    data-testid="gov-security-report-card-contact">
                <strong>{t('gov.security.reportCard.contactLabel')}:</strong>{' '}
                <a
                  href={`mailto:${t('gov.security.reportCard.contactEmail')}`}
                  data-testid="gov-security-report-card-email"
                  style={{ color: CYAN_STRONG, textDecoration: 'underline' }}
                >
                  {t('gov.security.reportCard.contactEmail')}
                </a>
              </span>
            </div>
          </div>
        </section>

        {/* v4.0 Phase 8 · Doctrine anchor link — trust page → demo (legacy). */}
        <section className="hussar-pol-op-section" data-testid="gov-security-doctrine-anchor-link">
          <Link
            to="/review-akt-imigracyjnych"
            data-testid="gov-security-anchor-to-demo"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#7DD3FC', fontWeight: 600, textDecoration: 'underline', fontSize: 15 }}
          >
            Zobacz demo weryfikacji akt przez człowieka
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </section>

        {/* v4.0 CTA row — repointed to record-integrity surfaces */}
        <section className="hussar-pol-op-section" data-testid="gov-security-cta-row">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <Link to="/review-akt-imigracyjnych" data-testid="gov-security-cta-review-demo"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 8, fontWeight: 700, background: CYAN, color: '#0B1220', textDecoration: 'none', fontSize: 15 }}>
              <FileText className="w-4 h-4" aria-hidden="true" />
              {t('gov.security.ctas.primaryLabel')}
            </Link>
            <Link to="/moje-akta-imigracyjne" data-testid="gov-security-cta-moje-akta"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 8, fontWeight: 600, background: 'rgba(15,23,42,0.55)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(226,232,240,0.92)', textDecoration: 'none', fontSize: 14 }}>
              <UserCheck className="w-4 h-4" aria-hidden="true" />
              {t('gov.security.ctas.secondaryLabel')}
            </Link>
            <Link to="/dla-instytucji" data-testid="gov-security-cta-dla-instytucji"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 8, fontWeight: 600, background: 'transparent', border: '1px solid rgba(148,163,184,0.30)', color: 'rgba(226,232,240,0.85)', textDecoration: 'none', fontSize: 14 }}>
              <Layers className="w-4 h-4" aria-hidden="true" />
              {t('gov.security.ctas.tertiaryLabel')}
            </Link>
          </div>
        </section>

        {/* Legacy 6-card section (retained for backward compatibility with existing i18n) */}
        <section className="hussar-pol-op-section" data-testid="gov-security-sections">
          <div className="hussar-agenci-deliverables-grid">
            {legacySections.map((s, i) => {
              const Icon = legacyIcons[i] || ShieldCheck;
              return (
                <Card key={s.title} className="hussar-agenci-deliverable-card" data-testid={`gov-security-card-${i}`}>
                  <div className="hussar-agenci-deliverable-icon"><Icon className="w-5 h-5" /></div>
                  <h3 className="hussar-agenci-deliverable-title" style={{ fontSize: '15px' }}>{s.title}</h3>
                  <p className="hussar-agenci-deliverable-body">{s.body}</p>
                </Card>
              );
            })}
          </div>
        </section>

        <section className="hussar-pol-op-section" data-testid="gov-security-disclaimer">
          <p className="hussar-tc-meta">{t('gov.security.disclaimer')}</p>
        </section>

        <IntegrityCapability testIdPrefix="gov-security-integrity" />

        <section className="hussar-pol-op-section hussar-pol-op-footer-cta" data-testid="gov-security-footer-cta">
          <h2 className="hussar-agenci-h2">{t('gov.security.footerCtaTitle')}</h2>
          <p className="hussar-pol-op-body">
            {t('gov.security.footerCtaBody')}
          </p>
          <div className="hussar-agenci-cta-row">
            <Button size="lg" onClick={() => navigate('/pilot-publiczny')}
                    className="hussar-btn-primary hussar-btn-large"
                    data-testid="gov-security-cta-pilot">
              {t('gov.security.footerCtaButton')} <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </section>

        <footer className="hussar-pol-op-footer" data-testid="gov-security-footer">
          <p>{t('gov.security.footerLine')}</p>
          <p className="hussar-pol-op-footer-meta">{t('gov.security.footerMeta')}</p>
        </footer>
      </main>
    </div>
  );
};

export default BezpieczenstwoIZgodnosc;
